import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { TourOverlay } from "../ui/tour-overlay"

const STORAGE_KEY = "kmk:tour-home:v1"

export interface TourStep {
	key: string
	target: string
	title: string
	description: string
}

const HOME_TOUR_STEPS: TourStep[] = [
	{
		key: "beginner",
		target: '[data-tour="beginner"]',
		title: "Начните отсюда",
		description:
			"Путь новичка это пошаговый план от нуля до первых подписчиков. Отмечайте пройденные шаги, и баннер покажет, сколько позади.",
	},
	{
		key: "categories",
		target: '[data-tour="categories"]',
		title: "Каталог",
		description:
			"Форматы стоят кружками, а темы и ниши открываются плитками под ними. Внутри можно переключаться, не возвращаясь назад.",
	},
	{
		key: "continue",
		target: '[data-tour="continue"]',
		title: "Продолжить просмотр",
		description: "Недосмотренные видео ждут здесь, с того места, где вы остановились.",
	},
	{
		key: "search",
		target: '[data-tour="search"]',
		title: "Поиск",
		description:
			"Кнопка в центре панели. Печатайте, выдача обновится сама, рядом фильтр по длительности.",
	},
	{
		key: "nav",
		target: '[data-tour="nav"]',
		title: "Навигация",
		description:
			"Слева материалы: главная и библиотека с историей просмотров и отложенным. Справа люди: сообщество с правилами и ответами и реферальная программа.",
	},
]

interface TourContextValue {
	isOpen: boolean
	step: TourStep | null
	stepIndex: number
	totalSteps: number
	isFirst: boolean
	isLast: boolean
	start: () => void
	next: () => void
	back: () => void
	skipMissing: () => void
	skip: () => void
}

const TourContext = createContext<TourContextValue | null>(null)

function hasSeenTour(): boolean {
	try {
		return localStorage.getItem(STORAGE_KEY) === "1"
	} catch {
		return false
	}
}

function markTourSeen() {
	try {
		localStorage.setItem(STORAGE_KEY, "1")
	} catch {}
}

export function shouldOfferHomeTour(): boolean {
	return !hasSeenTour()
}

export function TourProvider({ children }: { children: ReactNode }) {
	const [isOpen, setIsOpen] = useState(false)
	const [stepIndex, setStepIndex] = useState(0)
	const directionRef = useRef<1 | -1>(1)

	const close = useCallback(() => {
		setIsOpen(false)
		document.body.removeAttribute("data-tour-open")
		window.dispatchEvent(new Event("kmk:tour-closed"))
	}, [])

	const finish = useCallback(() => {
		markTourSeen()
		close()
	}, [close])

	const start = useCallback(() => {
		directionRef.current = 1
		setStepIndex(0)
		setIsOpen(true)
		document.body.setAttribute("data-tour-open", "true")
		window.dispatchEvent(new Event("kmk:tour-open"))
	}, [])

	const next = useCallback(() => {
		directionRef.current = 1
		setStepIndex((index) => {
			if (index + 1 >= HOME_TOUR_STEPS.length) {
				finish()
				return index
			}
			return index + 1
		})
	}, [finish])

	const back = useCallback(() => {
		directionRef.current = -1
		setStepIndex((index) => Math.max(0, index - 1))
	}, [])

	const skipMissing = useCallback(() => {
		if (directionRef.current === -1) {
			setStepIndex((index) => {
				if (index === 0) {
					directionRef.current = 1
					return 1
				}
				return index - 1
			})
			return
		}
		next()
	}, [next])

	useEffect(() => {
		const handleTourBack = () => finish()
		window.addEventListener("kmk:tour-back", handleTourBack)
		return () => window.removeEventListener("kmk:tour-back", handleTourBack)
	}, [finish])

	const value = useMemo<TourContextValue>(
		() => ({
			isOpen,
			step: isOpen ? HOME_TOUR_STEPS[stepIndex] : null,
			stepIndex,
			totalSteps: HOME_TOUR_STEPS.length,
			isFirst: stepIndex === 0,
			isLast: stepIndex === HOME_TOUR_STEPS.length - 1,
			start,
			next,
			back,
			skipMissing,
			skip: finish,
		}),
		[isOpen, stepIndex, finish, next, back, skipMissing, start],
	)

	return (
		<TourContext.Provider value={value}>
			{children}
			<TourOverlay />
		</TourContext.Provider>
	)
}

export function useTour() {
	const context = useContext(TourContext)
	if (!context) throw new Error("useTour must be used within TourProvider")
	return context
}
